import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, MapPin, Phone, Users, Shield, Target, Heart } from "lucide-react";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { AboutSEO } from "../components/SEO";
import api from "../lib/api";
import useFooterAddress from "../lib/useFooterAddress";

const ABOUT_DEFAULTS = {
  image: "https://images.unsplash.com/photo-1497366216548-37526070297c?w=800",
};

const AboutPage = () => {
  const [aboutContent, setAboutContent] = useState(ABOUT_DEFAULTS);
  const { mapEmbedUrl, address } = useFooterAddress();

  useEffect(() => {
    let mounted = true;
    api
      .getContentSection("about_page")
      .then((res) => {
        if (mounted && res?.content) {
          setAboutContent({ ...ABOUT_DEFAULTS, ...res.content });
        }
      })
      .catch(() => {});
    return () => {
      mounted = false;
    };
  }, []);

  const values = [
    {
      icon: Shield,
      title: "Trust & Integrity",
      description: "We operate with complete transparency. No hidden fees, no surprises—just honest, reliable service.",
    },
    {
      icon: Users,
      title: "Local Expertise",
      description: "Based in Batley, we know West Yorkshire inside out. Our local knowledge benefits both landlords and tenants.",
    },
    {
      icon: Target,
      title: "Results-Focused",
      description: "We're committed to reducing voids, protecting properties, and maximising rental returns for our landlords.",
    },
    {
      icon: Heart,
      title: "People First",
      description: "We treat every property as if it were our own and every client—landlord or tenant—with respect.",
    },
  ];

  return (
    <div data-testid="about-page">
      <AboutSEO />
      {/* Hero Section */}
      <section className="bg-navy text-white py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <span className="text-gold font-medium text-sm tracking-wider uppercase mb-4 block">
              About Us
            </span>
            <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-semibold leading-tight mb-6">
              Keystone Properties
            </h1>
            <p className="text-xl text-white/80 leading-relaxed">
              A locally based property management and lettings company serving landlords 
              and tenants across West Yorkshire.
            </p>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section className="py-20 md:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="font-heading text-3xl md:text-4xl font-semibold text-navy mb-6">
                Professional Property Management You Can Trust
              </h2>
              <div className="space-y-4 text-slate-600 leading-relaxed">
                <p>
                  Keystone Properties was founded with a simple mission: to provide landlords in West Yorkshire 
                  with professional, reliable property management that takes the stress out of letting.
                </p>
                <p>
                  Based in Batley, we serve landlords and tenants across Leeds, Bradford, Dewsbury, 
                  and the wider West Yorkshire region. Our team combines local market knowledge 
                  with professional standards to deliver a service that truly works.
                </p>
                <p>
                  Whether you're a first-time landlord looking for guidance or an experienced investor 
                  seeking efficient portfolio management, we're here to help you achieve your goals.
                </p>
              </div>
            </div>
            <div className="relative">
              <img
                src={aboutContent.image}
                alt="Keystone Properties — Professional team in West Yorkshire"
                className="rounded-lg shadow-2xl"
              />
              <div className="absolute -bottom-6 -right-6 bg-navy text-white p-6 rounded-lg shadow-xl hidden lg:block">
                <div className="flex items-center gap-3">
                  <MapPin className="w-6 h-6 text-gold" />
                  <div>
                    <div className="font-heading font-semibold">Batley, West Yorkshire</div>
                    <div className="text-sm text-white/70">Locally Based</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Mission Section */}
      <section className="py-20 md:py-28 bg-off-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto text-center mb-16">
            <h2 className="font-heading text-3xl md:text-4xl font-semibold text-navy mb-6">
              Our Mission
            </h2>
            <p className="text-xl text-slate-600 leading-relaxed">
              To be the most trusted property management company in West Yorkshire, 
              delivering exceptional service to landlords while ensuring tenants 
              enjoy well-maintained, professionally managed homes.
            </p>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-20 md:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="font-heading text-3xl md:text-4xl font-semibold text-navy mb-4">
              Our Values
            </h2>
            <p className="text-slate-600 max-w-2xl mx-auto">
              The principles that guide everything we do.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, index) => (
              <Card key={index} className="border-slate-200 hover:shadow-lg transition-shadow">
                <CardContent className="p-6 text-center">
                  <div className="w-14 h-14 rounded-full bg-navy/5 flex items-center justify-center mx-auto mb-6">
                    <value.icon className="w-7 h-7 text-navy" />
                  </div>
                  <h3 className="font-heading text-lg font-semibold text-navy mb-3">
                    {value.title}
                  </h3>
                  <p className="text-slate-600 text-sm">
                    {value.description}
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Office Info */}
      <section className="py-20 md:py-28 bg-off-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="font-heading text-3xl md:text-4xl font-semibold text-navy mb-6">
                Visit Our Office
              </h2>
              <p className="text-slate-600 mb-8 leading-relaxed">
                Our Batley office is centrally located and easily accessible. 
                Drop in for a chat about your property needs or arrange a meeting with our team.
              </p>
              <div className="space-y-4">
                <div className="flex items-start gap-4">
                  <MapPin className="w-6 h-6 text-gold flex-shrink-0" />
                  <div>
                    <div className="font-heading font-semibold text-navy">Address</div>
                    <div className="text-slate-600">17 Commercial Street, Batley, WF17 5HJ</div>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <Phone className="w-6 h-6 text-gold flex-shrink-0" />
                  <div>
                    <div className="font-heading font-semibold text-navy">Phone</div>
                    <a href="tel:01924 520163" className="text-slate-600 hover:text-gold">01924 520163</a>
                  </div>
                </div>
              </div>
            </div>
            <div className="h-80 rounded-lg overflow-hidden bg-slate-200 shadow-lg">
              <iframe
                src={mapEmbedUrl}
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title={`Keystone Properties Location - ${address}`}
                data-testid="about-map-iframe"
              ></iframe>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 md:py-28 bg-navy text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-heading text-3xl md:text-4xl font-semibold mb-6">
            Ready to Work With Us?
          </h2>
          <p className="text-white/80 text-lg mb-8 max-w-2xl mx-auto">
            Get in touch to discuss how we can help with your property needs.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button
              asChild
              size="lg"
              className="bg-gold hover:bg-gold-light text-navy font-semibold"
            >
              <Link to="/contact">
                Contact Us
                <ArrowRight className="ml-2 w-5 h-5" />
              </Link>
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AboutPage;
